import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import KpiCard from './KpiCard';
import {
  deactivateValet,
  forceActivateValet,
  authorizeValet,
  suspendValet,
} from '../../services/adminService';
import { VALET, FONTS, TYPE } from '../../theme';
import { MOBILE_DIALOG } from './responsive';

// ── Themed primitives ─────────────────────────────────────────────────────────

const SectionEyebrow = styled(Typography)({
  ...TYPE.eyebrow,
  marginBottom: '6px',
});

const SectionTitle = styled(Typography)({
  ...TYPE.sectionTitle,
  marginBottom: '16px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '14px',
  marginBottom: '32px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const TableWrapper = styled(Box)({
  backgroundColor: VALET.surface,
  border: `1px solid ${VALET.border}`,
  borderRadius: '14px',
  overflow: 'hidden',
  boxShadow: VALET.shadow,
});

const StyledHeaderCell = styled(TableCell)({
  fontFamily: FONTS.mono,
  fontSize: '10px',
  color: VALET.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.16em',
  fontWeight: 500,
  padding: '14px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  borderBottom: `1px solid ${VALET.border}`,
  backgroundColor: VALET.surfaceMuted,
});

const StyledCell = styled(TableCell)({
  padding: '14px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  fontSize: '13px',
  fontFamily: FONTS.sans,
  color: VALET.textPrimary,
  borderBottom: `1px solid ${VALET.border}`,
});

const MonoCell = styled(StyledCell)({
  fontFamily: FONTS.mono,
  fontSize: '12px',
});

const FilterChip = styled(Box)(({ selected }) => ({
  display: 'inline-block',
  padding: '6px 14px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid',
  borderColor: selected ? VALET.green : VALET.border,
  backgroundColor: selected ? VALET.green : VALET.bg,
  color: selected ? '#ffffff' : VALET.textSecondary,
  fontFamily: FONTS.sans,
  userSelect: 'none',
  transition: 'all 160ms ease',
  '&:hover': {
    borderColor: selected ? VALET.green : VALET.borderStrong,
    backgroundColor: selected ? VALET.greenDark : VALET.surfaceHover,
  },
}));

const SectionDivider = styled(Box)({
  height: '1px',
  backgroundColor: VALET.bronzeHairline,
  margin: '36px 0',
});

// ── Color helpers (now warmer / lighter to match the editorial palette) ──────

function getAcceptColor(rate) {
  const val = parseFloat(rate);
  if (val >= 95) return VALET.success;
  if (val >= 90) return VALET.warning;
  return VALET.danger;
}

function getMissedColor(rate) {
  const val = parseFloat(rate);
  if (val <= 5) return VALET.success;
  if (val <= 10) return VALET.warning;
  return VALET.danger;
}

// ── Roster helpers ────────────────────────────────────────────────────────────

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isActive(v) {
  if (typeof v.active === 'boolean') return v.active;
  if (typeof v.isActive === 'boolean') return v.isActive;
  if (v.status) return String(v.status).toLowerCase() === 'active';
  return true;
}

// Status pill palettes for the new editorial light theme. Each entry is a
// semantic background + foreground pair pulled from the central theme.
const ONBOARDING_LABELS = {
  active: {
    label: 'Active',
    bg: VALET.successBg,
    fg: VALET.success,
    border: `${VALET.success}33`,
  },
  pending_certn: {
    label: 'Pending Screening',
    bg: VALET.bronzeMuted,
    fg: VALET.bronze,
    border: VALET.bronzeHairline,
  },
  pending_documents: {
    label: 'Pending Documents',
    bg: VALET.bronzeMuted,
    fg: VALET.bronze,
    border: VALET.bronzeHairline,
  },
  pending_provider_approval: {
    label: 'Pending Approval',
    bg: VALET.infoBg,
    fg: VALET.info,
    border: `${VALET.info}33`,
  },
  certn_failed: {
    label: 'Screening Failed',
    bg: VALET.dangerBg,
    fg: VALET.danger,
    border: `${VALET.danger}33`,
  },
  suspended_admin: {
    label: 'Suspended',
    bg: VALET.dangerBg,
    fg: VALET.danger,
    border: `${VALET.danger}33`,
  },
};

function statusInfo(v) {
  if (v.isDeletedFlag) {
    return {
      label: 'Deleted',
      bg: VALET.surfaceMuted,
      fg: VALET.textMuted,
      border: VALET.border,
    };
  }
  const onboarding = v.valetOnboardingStatus;
  if (onboarding && ONBOARDING_LABELS[onboarding]) {
    return ONBOARDING_LABELS[onboarding];
  }
  return isActive(v)
    ? ONBOARDING_LABELS.active
    : {
        label: 'Inactive',
        bg: VALET.surfaceMuted,
        fg: VALET.textMuted,
        border: VALET.border,
      };
}

function rowId(v) {
  return v._id || v.id || v.valetId;
}

function backgroundCheckSubline(v) {
  const bc = v.backgroundCheck;
  if (!bc || !bc.status || bc.status === 'not_started') return null;
  const provider = bc.provider === 'yardstik'
    ? 'Yardstik'
    : bc.provider === 'certn'
      ? 'Certn'
      : bc.provider === 'grandfathered'
        ? 'Grandfathered'
        : null;
  const statusLabel = bc.status.charAt(0).toUpperCase() + bc.status.slice(1);
  const dateBit = bc.completedAt ? ` · ${formatDate(bc.completedAt)}` : '';
  const rawBit = bc.status === 'failed' && bc.certnResult ? ` (${bc.certnResult})` : '';
  if (bc.provider === 'grandfathered') return 'Grandfathered';
  if (!provider) return `${statusLabel}${rawBit}${dateBit}`;
  return `${provider} · ${statusLabel}${rawBit}${dateBit}`;
}

// Yardstik dashboard URL for a given report. We only ever pass the user
// through to Yardstik's UI — no report data is stored on our side.
function yardstikReportUrl(v) {
  const bc = v.backgroundCheck;
  if (!bc || bc.provider !== 'yardstik' || !bc.certnApplicationId) return null;
  return `https://app.yardstik.com/reports/${bc.certnApplicationId}`;
}

function extractValets(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.valets)) return data.valets;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function matchesSearch(v, q) {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    (v.name || v.fullName || v.valetName || '').toLowerCase().includes(lower) ||
    (v.phone || v.phoneNumber || '').toLowerCase().includes(lower) ||
    (v.email || '').toLowerCase().includes(lower)
  );
}

function mergeValetData(data) {
  const arrays = [
    { key: 'jobsPerValet', field: 'completed' },
    { key: 'jobsPerValetHour', field: 'jobsPerHour' },
    { key: 'dispatchAcceptTime', field: 'dispatchAccept' },
    { key: 'acceptanceRate', field: 'acceptRate' },
    { key: 'missedRequests', field: 'missedRate' },
    { key: 'pickupDistance', field: 'pickupDist' },
    { key: 'parkingSearchTime', field: 'parkSearch' },
    { key: 'retrievalTime', field: 'retrieval' },
    { key: 'idleTime', field: 'idleTime' },
    { key: 'aspMovesPerHour', field: 'aspPerHour' },
  ];

  const valetMap = {};
  for (const { key, field } of arrays) {
    const arr = data[key];
    if (!Array.isArray(arr)) continue;
    arr.forEach((item) => {
      const id = item.valetId || item.id;
      if (!id) return;
      if (!valetMap[id]) {
        valetMap[id] = { valetId: id, valetName: item.valetName || item.name || '' };
      }
      valetMap[id][field] = item.value ?? item[field] ?? null;
      if (item.valetName || item.name) {
        valetMap[id].valetName = item.valetName || item.name;
      }
    });
  }
  return Object.values(valetMap);
}

// ── Roster card with pills + Approve/Reject/Force-Activate actions ────────────

function RosterSection({ rosterData, rosterError }) {
  const [rows, setRows] = useState(() => extractValets(rosterData));
  const [pending, setPending] = useState({});
  const [toast, setToast] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  React.useEffect(() => {
    setRows(extractValets(rosterData));
  }, [rosterData]);

  const handleActionClick = (valet) => {
    if (isActive(valet)) {
      setConfirmTarget(valet);
    } else {
      performToggle(valet);
    }
  };

  const performToggle = async (valet) => {
    const id = rowId(valet);
    if (!id) return;
    const currentlyActive = isActive(valet);
    const nextActive = !currentlyActive;

    setRows((prev) => prev.map((r) => rowId(r) === id ? { ...r, active: nextActive, isActive: nextActive, status: nextActive ? 'active' : 'inactive' } : r));
    setPending((p) => ({ ...p, [id]: true }));

    try {
      if (currentlyActive) {
        await deactivateValet(id);
      } else {
        await forceActivateValet(id);
      }
      setToast({ severity: 'success', msg: `${valet.name || valet.fullName || 'Valet'} ${nextActive ? 'activated' : 'deactivated'}.` });
    } catch (err) {
      setRows((prev) => prev.map((r) => rowId(r) === id ? valet : r));
      setToast({ severity: 'error', msg: err.message || 'Action failed' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const handleConfirmDeactivate = () => {
    const valet = confirmTarget;
    setConfirmTarget(null);
    performToggle(valet);
  };

  const performApprove = async (valet) => {
    const id = rowId(valet);
    if (!id) return;
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await authorizeValet(id);
      setRows((prev) => prev.map((r) => rowId(r) === id ? {
        ...r,
        valetOnboardingStatus: 'active',
        isActive: true,
        active: true,
      } : r));
      setToast({ severity: 'success', msg: `${valet.name || 'Valet'} approved.` });
    } catch (err) {
      setToast({ severity: 'error', msg: err.message || 'Approve failed' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const performReject = async (valet) => {
    const id = rowId(valet);
    if (!id) return;
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await suspendValet(id, 'Rejected by admin at approval step');
      setRows((prev) => prev.map((r) => rowId(r) === id ? {
        ...r,
        valetOnboardingStatus: 'suspended_admin',
        isActive: false,
        active: false,
      } : r));
      setToast({ severity: 'success', msg: `${valet.name || 'Valet'} rejected.` });
    } catch (err) {
      setToast({ severity: 'error', msg: err.message || 'Reject failed' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  if (rosterError) {
    return (
      <Typography sx={{
        color: VALET.danger,
        backgroundColor: VALET.dangerBg,
        fontSize: '13px',
        textAlign: 'center',
        padding: '18px',
        borderRadius: '8px',
        fontFamily: FONTS.sans,
      }}>
        {rosterError}
      </Typography>
    );
  }

  if (rosterData === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
        <CircularProgress size={28} sx={{ color: VALET.green }} />
      </Box>
    );
  }

  const filtered = rows.filter((v) => {
    if (statusFilter === 'active' && !isActive(v)) return false;
    if (statusFilter === 'inactive' && isActive(v)) return false;
    return matchesSearch(v, search);
  });

  const pendingApprovalCount = rows.filter((v) => v.valetOnboardingStatus === 'pending_provider_approval').length;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <Box>
          <SectionEyebrow>Roster</SectionEyebrow>
          <SectionTitle sx={{ marginBottom: 0 }}>
            Valets ({filtered.length}{filtered.length !== rows.length ? ` of ${rows.length}` : ''})
            {pendingApprovalCount > 0 && (
              <Box component="span" sx={{
                marginLeft: '12px',
                fontFamily: FONTS.sans,
                fontSize: '12px',
                fontWeight: 500,
                color: VALET.info,
                backgroundColor: VALET.infoBg,
                padding: '4px 10px',
                borderRadius: '999px',
                verticalAlign: 'middle',
              }}>
                {pendingApprovalCount} awaiting your approval
              </Box>
            )}
          </SectionTitle>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search name, phone, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            flex: '1 1 280px',
            maxWidth: 360,
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '10px',
              fontFamily: FONTS.sans,
              backgroundColor: VALET.bg,
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['all', 'active', 'inactive'].map((f) => (
            <FilterChip key={f} selected={statusFilter === f} onClick={() => setStatusFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </FilterChip>
          ))}
        </Box>
      </Box>

      <TableWrapper>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>Name</StyledHeaderCell>
                <StyledHeaderCell>Contact</StyledHeaderCell>
                <StyledHeaderCell>Status</StyledHeaderCell>
                <StyledHeaderCell>Joined</StyledHeaderCell>
                <StyledHeaderCell align="right">Action</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((v, idx) => {
                const id = rowId(v);
                const active = isActive(v);
                const busy = !!pending[id];
                const status = statusInfo(v);
                const isPendingApproval = v.valetOnboardingStatus === 'pending_provider_approval';
                const isOverrideCase =
                  !active &&
                  v.valetOnboardingStatus &&
                  v.valetOnboardingStatus !== 'active' &&
                  !isPendingApproval;
                return (
                  <TableRow key={id || idx} sx={{
                    transition: 'background-color 160ms ease',
                    '&:hover td': { backgroundColor: VALET.surfaceHover },
                    '&:last-child td': { borderBottom: 'none' },
                  }}>
                    <StyledCell sx={{ fontWeight: 600 }}>{v.name || v.fullName || v.valetName || '—'}</StyledCell>
                    <StyledCell sx={{ color: VALET.textSecondary }}>{v.phone || v.phoneNumber || v.email || '—'}</StyledCell>
                    <StyledCell>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          backgroundColor: status.bg,
                          color: status.fg,
                          border: `1px solid ${status.border || 'transparent'}`,
                          fontSize: '10px',
                          fontWeight: 600,
                          padding: '4px 10px',
                          borderRadius: '999px',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontFamily: FONTS.mono,
                        }}
                      >
                        {status.label}
                      </Box>
                      {backgroundCheckSubline(v) && (
                        <Box sx={{
                          marginTop: '6px',
                          fontSize: '11px',
                          color: VALET.textMuted,
                          fontFamily: FONTS.mono,
                        }}>
                          {backgroundCheckSubline(v)}
                          {yardstikReportUrl(v) && (
                            <>
                              {' · '}
                              <Box
                                component="a"
                                href={yardstikReportUrl(v)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: VALET.green,
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                View report ↗
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </StyledCell>
                    <StyledCell sx={{ fontFamily: FONTS.mono, fontSize: '12px', color: VALET.textMuted }}>
                      {formatDate(v.createdAt || v.joinedAt)}
                    </StyledCell>
                    <StyledCell align="right">
                      {isPendingApproval ? (
                        <Box sx={{ display: 'inline-flex', gap: '8px' }}>
                          <Button
                            size="small"
                            disabled={busy || !id}
                            onClick={() => performApprove(v)}
                            sx={{
                              textTransform: 'none',
                              fontSize: '12px',
                              fontWeight: 600,
                              fontFamily: FONTS.sans,
                              color: '#ffffff',
                              backgroundColor: VALET.green,
                              padding: '6px 14px',
                              minWidth: 88,
                              borderRadius: '8px',
                              boxShadow: 'none',
                              transition: 'all 160ms ease',
                              '&:hover': {
                                backgroundColor: VALET.greenDark,
                                boxShadow: VALET.shadow,
                              },
                            }}
                            variant="contained"
                            disableElevation
                          >
                            {busy ? '…' : 'Approve'}
                          </Button>
                          <Button
                            size="small"
                            disabled={busy || !id}
                            onClick={() => performReject(v)}
                            sx={{
                              textTransform: 'none',
                              fontSize: '12px',
                              fontWeight: 600,
                              fontFamily: FONTS.sans,
                              color: VALET.danger,
                              border: `1px solid ${VALET.danger}55`,
                              backgroundColor: VALET.bg,
                              padding: '6px 14px',
                              minWidth: 88,
                              borderRadius: '8px',
                              transition: 'all 160ms ease',
                              '&:hover': {
                                backgroundColor: VALET.dangerBg,
                              },
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          disabled={busy || !id}
                          onClick={() => handleActionClick(v)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: FONTS.sans,
                            color: active ? VALET.danger : (isOverrideCase ? VALET.bronze : VALET.success),
                            border: '1px solid',
                            borderColor: active ? `${VALET.danger}55` : (isOverrideCase ? VALET.bronzeHairline : `${VALET.success}55`),
                            backgroundColor: VALET.bg,
                            padding: '6px 14px',
                            minWidth: 118,
                            borderRadius: '8px',
                            transition: 'all 160ms ease',
                            '&:hover': {
                              backgroundColor: active
                                ? VALET.dangerBg
                                : (isOverrideCase ? VALET.bronzeMuted : VALET.successBg),
                            },
                          }}
                        >
                          {busy
                            ? '…'
                            : active
                            ? 'Deactivate'
                            : isOverrideCase
                            ? 'Force Activate'
                            : 'Reactivate'}
                        </Button>
                      )}
                    </StyledCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <StyledCell colSpan={5} sx={{ textAlign: 'center', color: VALET.textMuted, padding: '32px 16px' }}>
                    {rows.length === 0 ? 'No valets found.' : 'No results match your search.'}
                  </StyledCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableWrapper>

      <Dialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        PaperProps={{ sx: { borderRadius: '14px', padding: '6px', fontFamily: FONTS.sans } }}
        sx={MOBILE_DIALOG}
      >
        <DialogTitle sx={{ fontFamily: FONTS.display, fontSize: '20px', fontWeight: 500, letterSpacing: '-0.02em', color: VALET.ink }}>
          Deactivate {confirmTarget?.name || confirmTarget?.fullName || confirmTarget?.valetName || 'this valet'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px', color: VALET.textSecondary, fontFamily: FONTS.sans }}>
            This valet will stop receiving new job requests. You can reactivate them at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 18px 14px' }}>
          <Button
            onClick={() => setConfirmTarget(null)}
            sx={{ textTransform: 'none', fontSize: '13px', color: VALET.textSecondary, fontFamily: FONTS.sans }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: VALET.danger,
              color: '#ffffff',
              fontFamily: FONTS.sans,
              fontWeight: 600,
              '&:hover': { backgroundColor: '#7d241e' },
              borderRadius: '8px',
              padding: '6px 16px',
            }}
            variant="contained"
            disableElevation
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

function buildValetRows(data, rosterData) {
  const fromMetrics = mergeValetData(data || {});
  const byId = {};
  fromMetrics.forEach((v) => {
    const id = v.valetId || v.id;
    if (id) byId[id] = { ...v, valetId: id };
  });
  const roster = extractValets(rosterData);
  roster.forEach((v) => {
    const id = v._id || v.id || v.valetId;
    if (!id) return;
    const name =
      v.valetName ||
      v.name ||
      v.fullName ||
      `${v.firstName || ''} ${v.lastName || ''}`.trim() ||
      '';
    byId[id] = {
      ...byId[id],
      valetId: id,
      valetName: name || byId[id]?.valetName || '',
      completed: v.completed ?? byId[id]?.completed,
      jobsPerHour: v.jobsPerHour ?? v.aspPerHour ?? byId[id]?.jobsPerHour,
      dispatchAccept: v.dispatchAccept ?? byId[id]?.dispatchAccept,
      acceptRate: v.acceptRate ?? byId[id]?.acceptRate,
      missedRate: v.missedRate ?? byId[id]?.missedRate,
      pickupDist: v.pickupDist ?? byId[id]?.pickupDist,
      parkSearch: v.parkSearch ?? byId[id]?.parkSearch,
      retrieval: v.retrieval ?? byId[id]?.retrieval,
      idleTime: v.idleTime ?? byId[id]?.idleTime,
      aspPerHour: v.aspPerHour ?? byId[id]?.aspPerHour,
    };
  });
  return Object.values(byId).filter((v) => v.valetName || v.completed != null);
}

export default function ValetsTab({ data, rosterData, rosterError }) {
  if (!data) return null;
  const d = data;
  const fleet = d.fleetAverages || d;
  const valets = buildValetRows(d, rosterData);
  const maxCompleted = valets.length > 0 ? Math.max(...valets.map(v => v.completed || 0), 1) : 1;

  return (
    <Box>
      <SectionEyebrow>Fleet averages</SectionEyebrow>
      <SectionTitle>Performance at a glance</SectionTitle>
      <Grid4>
        <KpiCard label="Avg jobs per valet" value={fleet.avgJobsPerValet || '—'} subtitle="this period" />
        <KpiCard label="Acceptance rate" value={fleet.avgAcceptanceRate || '—'} subtitle="target > 92%" />
        <KpiCard label="Missed rate" value={fleet.avgMissedRate || '—'} subtitle="target < 8%" />
        <KpiCard label="Dispatch → accept" value={fleet.avgDispatchAccept || '—'} subtitle="response latency" />
      </Grid4>

      {valets.length > 0 && (
        <>
          <SectionEyebrow>Per-valet</SectionEyebrow>
          <SectionTitle>Individual breakdown</SectionTitle>
          <TableWrapper>
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell>Valet</StyledHeaderCell>
                    <StyledHeaderCell>Jobs completed</StyledHeaderCell>
                    <StyledHeaderCell>Jobs/hr</StyledHeaderCell>
                    <StyledHeaderCell>Dispatch→accept</StyledHeaderCell>
                    <StyledHeaderCell>Accept rate</StyledHeaderCell>
                    <StyledHeaderCell>Missed rate</StyledHeaderCell>
                    <StyledHeaderCell>Pickup dist</StyledHeaderCell>
                    <StyledHeaderCell>Park search</StyledHeaderCell>
                    <StyledHeaderCell>Retrieval</StyledHeaderCell>
                    <StyledHeaderCell>Idle time</StyledHeaderCell>
                    <StyledHeaderCell>ASP/hr</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {valets.map((v, idx) => (
                    <TableRow key={v.valetId || idx} sx={{ '&:hover td': { backgroundColor: VALET.surfaceHover }, '&:last-child td': { borderBottom: 'none' } }}>
                      <StyledCell sx={{ fontWeight: 600 }}>{v.valetName || v.name || '—'}</StyledCell>
                      <StyledCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Box sx={{ flex: 1, height: 6, backgroundColor: VALET.surfaceHover, borderRadius: '3px', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${Math.round((v.completed || 0) / maxCompleted * 100)}%`, backgroundColor: VALET.green, borderRadius: '3px' }} />
                          </Box>
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: '12px', minWidth: '36px', textAlign: 'right', color: VALET.textPrimary }}>
                            {v.completed ?? '—'}
                          </Typography>
                        </Box>
                      </StyledCell>
                      <MonoCell>{v.jobsPerHour ?? '—'}</MonoCell>
                      <MonoCell>{v.dispatchAccept != null ? `${v.dispatchAccept}s` : '—'}</MonoCell>
                      <MonoCell sx={{ color: v.acceptRate != null ? getAcceptColor(v.acceptRate) : 'inherit', fontWeight: 600 }}>
                        {v.acceptRate != null ? `${v.acceptRate}%` : '—'}
                      </MonoCell>
                      <MonoCell sx={{ color: v.missedRate != null ? getMissedColor(v.missedRate) : 'inherit', fontWeight: 600 }}>
                        {v.missedRate != null ? `${v.missedRate}%` : '—'}
                      </MonoCell>
                      <MonoCell>{v.pickupDist != null ? `${v.pickupDist} mi` : '—'}</MonoCell>
                      <MonoCell>{v.parkSearch != null ? `${v.parkSearch} min` : '—'}</MonoCell>
                      <MonoCell>{v.retrieval != null ? `${v.retrieval} min` : '—'}</MonoCell>
                      <MonoCell>{v.idleTime != null ? `${v.idleTime} min` : '—'}</MonoCell>
                      <MonoCell>{v.aspPerHour ?? '—'}</MonoCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TableWrapper>
        </>
      )}

      <SectionDivider />

      <RosterSection rosterData={rosterData} rosterError={rosterError} />
    </Box>
  );
}
